/* eslint-disable react-hooks/exhaustive-deps */
import { tokens } from "../../../theme";
import { useEffect, useMemo, useState } from "react";
import { Box, useTheme } from "@mui/material";
import ServiceFilterBar from "./FilterBar";
import ServiceCommitRow from "./ServiceCommitRow";
import PatchBlock from "../ReleaseView/PatchBlock";
import EmptyState from "../ReleaseView/EmptyState";
import { releaseStyles } from "../styles";
import {
  adaptServiceView,
  serviceSegmentCounts,
  serviceMatchesSegment,
  serviceMatchesQuery,
  plural,
} from "./serviceViewHelpers";

const SEARCH_DEBOUNCE_MS = 150;
const NO_FILTERS = { services: [], releases: [], patches: [], segment: "all" };
const SERVICE_COLUMNS = [
  "Release",
  "Patch",
  "Tag",
  "Hash",
  "Message",
  "Flag",
  "PR",
];
const unique = (values) => Array.from(new Set(values));

const ServiceView = ({ getReleasesResp }) => {
  const theme = useTheme();
  const { releaseView } = releaseStyles(tokens(theme.paletteId));

  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!Array.isArray(getReleasesResp?.response)) return;
    const adapted = adaptServiceView(getReleasesResp.response);
    setServices(adapted);
    const first = adapted.find((svc) => svc.commits.length);
    setExpanded(new Set(first ? [first.key] : []));
  }, [getReleasesResp]);

  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(searchInput.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  const serviceOptions = useMemo(
    () => services.map((s) => s.repoName),
    [services]
  );

  const scopedServices = useMemo(
    () =>
      filters.services.length
        ? services.filter((s) => filters.services.includes(s.repoName))
        : services,
    [services, filters.services]
  );

  const releaseOptions = useMemo(
    () => unique(scopedServices.flatMap((s) => s.releases)).sort(),
    [scopedServices]
  );

  const patchOptions = useMemo(
    () => unique(scopedServices.flatMap((s) => s.patches)).sort(),
    [scopedServices]
  );

  // Narrow by release + patch + search; segment counts come from this pool.
  const narrowedServices = useMemo(
    () =>
      scopedServices.map((svc) => ({
        ...svc,
        commits: svc.commits.filter(
          (c) =>
            (!filters.releases.length ||
              filters.releases.includes(c.releaseLabel)) &&
            (!filters.patches.length ||
              filters.patches.includes(c.patchLabel)) &&
            serviceMatchesQuery(c, query)
        ),
      })),
    [scopedServices, filters.releases, filters.patches, query]
  );

  const counts = useMemo(
    () => serviceSegmentCounts(narrowedServices.flatMap((s) => s.commits)),
    [narrowedServices]
  );

  const visibleServices = useMemo(
    () =>
      narrowedServices.map((svc) => ({
        ...svc,
        commits: svc.commits.filter((c) =>
          serviceMatchesSegment(c, filters.segment)
        ),
        // PatchBlock reads patch.label for the header
        label: svc.repoName,
      })),
    [narrowedServices, filters.segment]
  );

  const totalCommits = visibleServices.reduce(
    (n, s) => n + s.commits.length,
    0
  );

  const changeFilter = (key, value) =>
    setFilters((prev) => {
      if (key === "services")
        return { ...prev, services: value, releases: [], patches: [] };
      if (key === "releases") return { ...prev, releases: value, patches: [] };
      return { ...prev, [key]: value };
    });

  const clearFilters = () => {
    setFilters(NO_FILTERS);
    setSearchInput("");
    setQuery("");
  };

  const allExpanded =
    visibleServices.length > 0 &&
    visibleServices.every((s) => expanded.has(s.key));
  const toggleAll = () =>
    setExpanded(
      allExpanded ? new Set() : new Set(visibleServices.map((s) => s.key))
    );
  const toggleService = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const serviceHeaderRowSx = { ...releaseView.serviceHeaderRow };

  return (
    <Box sx={releaseView.container}>
      <ServiceFilterBar
        serviceOptions={serviceOptions}
        releaseOptions={releaseOptions}
        patchOptions={patchOptions}
        filters={filters}
        onFilterChange={changeFilter}
        onClearAll={clearFilters}
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        counts={counts}
        allExpanded={allExpanded}
        onToggleAll={toggleAll}
      />

      {totalCommits ? (
        visibleServices.map((svc) => {
          const relCount = new Set(svc.commits.map((c) => c.releaseLabel)).size;
          const patchCount = new Set(svc.commits.map((c) => c.patchLabel)).size;
          const doubles = svc.commits.filter((c) => c.isDouble).length;

          const chips = (
            <>
              <Box component="span" sx={releaseView.patchCount(false)}>
                {plural(relCount, "release")}
              </Box>
              <Box
                component="span"
                sx={releaseView.patchCount(false)}
              >{`${patchCount} ${patchCount === 1 ? "patch" : "patches"}`}</Box>
              <Box component="span" sx={releaseView.patchCount(false)}>
                {plural(svc.commits.length, "total commit")}
              </Box>
              {doubles > 0 && (
                <Box component="span" sx={releaseView.patchCount(true)}>
                  {doubles} double
                </Box>
              )}
            </>
          );

          return (
            <PatchBlock
              key={svc.key}
              patch={svc}
              open={expanded.has(svc.key)}
              onToggle={() => toggleService(svc.key)}
              columns={SERVICE_COLUMNS}
              renderRow={(commit) => (
                <ServiceCommitRow key={commit.id} commit={commit} />
              )}
              headerRowSx={serviceHeaderRowSx}
              chips={chips}
            />
          );
        })
      ) : (
        <EmptyState
          patchCount={scopedServices.length}
          releases={scopedServices.map((s) => ({
            label: s.repoName,
            patchLabels: s.patches,
          }))}
          reason={
            !scopedServices.some((s) => s.commits.length)
              ? "these services carry no commits at all"
              : query
              ? `nothing matches "${query}"`
              : filters.segment === "double"
              ? "none of their commits are double commits"
              : filters.segment === "original"
              ? "every commit here is a double commit"
              : filters.segment === "noTicket"
              ? "every commit already has a ticket"
              : "the current filters exclude every commit"
          }
          onClear={clearFilters}
        />
      )}
    </Box>
  );
};

export default ServiceView;
